import React, { useCallback, useEffect, useState } from 'react';

import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';

import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

import { TemplateVisualBuilder } from '@/components/assessment-engine/template-builder';
import { TemplateBuilderTabs, type TemplateBuilderTab } from '@/components/assessment-engine/template-builder/TemplateBuilderTabs';
import { TemplateSettingsPanel } from '@/components/assessment-engine/template-builder/TemplateSettingsPanel';
import { LogicRulesEditor } from '@/components/assessment-engine/template-builder/LogicRulesEditor';

import { AePageHeader, AePageShell } from '@/components/assessment-engine/layout';

import {
  AE_BTN_PRIMARY,
  AE_BTN_SUCCESS,
  AE_LINK,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';

import { assessmentEngineService } from '@/services/assessmentEngineService';
import { useAssessmentPreview } from '@/components/assessment-engine/preview';

import type { AeTemplateDetail } from '@/types/assessmentEngine';

/** Platform admin — visual WYSIWYG template structure builder. */
export const TemplateBuilderPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TemplateBuilderTab =
    tabParam === 'design' || tabParam === 'logic' ? tabParam : 'settings';
  const [template, setTemplate] = useState<AeTemplateDetail | null>(null);
  const [publishing, setPublishing] = useState(false);
  const { openTemplatePreview } = useAssessmentPreview();

  const load = useCallback(() => {
    if (!templateId) return;
    assessmentEngineService
      .getTemplate(templateId)
      .then(setTemplate)
      .catch(() => toast.error(t('assessmentEngine.templates.failedLoad')));
  }, [templateId, t]);

  const reload = load;

  useEffect(() => {
    load();
  }, [load]);

  const isDraft = template?.status === 'draft';
  const isFrozen = !!template?.frozen_at;
  const structureReadOnly = !isDraft || isFrozen;

  const setActiveTab = (tab: TemplateBuilderTab) => {
    if (tab === 'settings') {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ tab }, { replace: true });
  };

  const handlePublish = async () => {
    if (!templateId || !template) return;
    if (!window.confirm(t('assessmentEngine.templates.publishConfirm', { name: template.name }))) return;
    setPublishing(true);
    try {
      await assessmentEngineService.publishTemplate(templateId);
      toast.success(t('assessmentEngine.templates.published'));
      load();
    } catch {
      toast.error(t('assessmentEngine.templates.actionFailed'));
    } finally {
      setPublishing(false);
    }
  };

  const handleClone = async () => {
    if (!template) return;
    try {
      const suffix = Date.now().toString(36).slice(-4).toUpperCase();
      const cloned = await assessmentEngineService.cloneTemplate(template.id, {
        code: `${template.code}-EDIT-${suffix}`,
        name: `${template.name} (edit)`,
      });
      navigate(`/assessment-engine/templates/${cloned.id}`);
    } catch {
      toast.error(t('assessmentEngine.templates.actionFailed'));
    }
  };

  if (!template) {
    return (
      <AePageShell>
        <p className={AE_MUTED}>{t('assessmentEngine.templates.loading')}</p>
      </AePageShell>
    );
  }

  return (
    <AePageShell>
      <AePageHeader
        title={template.name}
        description={`${template.code} · ${t(`assessmentEngine.templateStatus.${template.status}`)}`}
        breadcrumbs={[
          { label: t('assessmentEngine.breadcrumbs.engine'), to: '/assessment-engine/campaigns' },
          { label: t('assessmentEngine.templates.title'), to: '/assessment-engine/templates' },
          { label: template.name },
        ]}
        actions={
          <div className="flex gap-2 items-center">
            <button
              type="button"
              className={AE_BTN_PRIMARY}
              onClick={() => templateId && openTemplatePreview(templateId)}
            >
              <EyeIcon className="inline h-5 w-5 mr-1 -mt-0.5" aria-hidden />
              {t('assessmentEngine.preview.button')}
            </button>
            {isDraft && (
              <button type="button" disabled={publishing} className={AE_BTN_SUCCESS} onClick={handlePublish}>
                {t('assessmentEngine.templates.publish')}
              </button>
            )}
            {!isDraft && (
              <button type="button" className={AE_BTN_PRIMARY} onClick={handleClone}>
                {t('assessmentEngine.templates.cloneToEdit')}
              </button>
            )}
            <Link to="/assessment-engine/templates" className={AE_LINK}>
              {t('assessmentEngine.campaign.cancel')}
            </Link>
          </div>
        }
      />

      {(isFrozen || !isDraft) && (
        <div className="bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 text-[var(--color-warning)] text-sm rounded p-3 mb-4">
          {t('assessmentEngine.builder.readOnly')}
        </div>
      )}

      <TemplateBuilderTabs active={activeTab} onChange={setActiveTab} />

      {/* Keep tabs mounted so debounced autosave timers are not cleared on tab switch. */}
      <div className={activeTab === 'design' ? undefined : 'hidden'}>
        <TemplateVisualBuilder
          template={template}
          structureReadOnly={structureReadOnly}
          onReload={reload}
        />
      </div>
      <div className={activeTab === 'logic' ? undefined : 'hidden'}>
        <LogicRulesEditor template={template} readOnly={structureReadOnly} onReload={reload} />
      </div>
      <div className={activeTab === 'settings' ? undefined : 'hidden'}>
        <TemplateSettingsPanel template={template} readOnly={structureReadOnly} onReload={reload} />
      </div>
    </AePageShell>
  );
};
